// theme/presets/accents.ts — accent colour presets.
//
// Each preset maps to { light: {…}, dark: {…} } overrides for the five
// brand/accent tokens in ThemeTokens['color'].
//
// 1.25.00: every non-default preset is DERIVED from one seed per mode through
// the same ramp function the custom colour wheel uses (applyPreferences'
// resolveAccent), so a preset and a custom pick can never disagree about what
// "dim" or "secondary" means. Seeds are chosen as colours, not as contrast
// maths: the old table shipped #39ff14 ("neon green") and #7fff00 (chartreuse)
// because they cleared a ratio, and they read as a monitor test pattern.
//
// Every seed is asserted ≥ 4.5:1 as text on its mode's card surface, and its
// onAccent pairing ≥ 4.5:1 on the fill, in accents.test.ts — so the table
// cannot drift out of AA by hand-editing a hex.
//
// 'default' mirrors tokens.ts VERBATIM and is not derived: a member whose
// stored pref is `accent: 'default'` (everyone who never opened the wheel) is
// themed from THIS entry, so it must be byte-identical to the base tokens.

import type { AccentKey } from '@/core/models/preferences';
import { darken, lighten } from './color';

export interface AccentColors {
  brandFrom: string;
  brandTo: string;
  accentCyan: string;
  accentMagenta: string;
  accentAmber: string;
}

export interface AccentPreset {
  light: AccentColors;
  dark: AccentColors;
}

/** The one ramp. Identical to what a custom hex gets in applyPreferences. */
export function rampFrom(seed: string): AccentColors {
  return {
    brandFrom: seed,
    brandTo: darken(seed, 0.25),
    accentCyan: seed,
    accentMagenta: lighten(seed, 0.12),
    accentAmber: darken(seed, 0.15),
  };
}

/** Seed per preset per mode. Dark seeds sit light enough to read as text on the
 *  near-black card; light seeds sit deep enough to read on white. */
export const PRESET_SEEDS: Record<Exclude<AccentKey, 'custom' | 'default'>, { dark: string; light: string }> = {
  teal:   { dark: '#5CC4A9', light: '#0B7A5C' }, // verdigris, not the old neon #00FFB2
  sunset: { dark: '#E08A5A', light: '#A3471C' }, // terracotta
  aurora: { dark: '#6FB7C9', light: '#0E6B80' }, // sea-glass blue
  ember:  { dark: '#E36A5A', light: '#A8321F' }, // brick
  mint:   { dark: '#7FC77A', light: '#2F7A35' }, // leaf, not chartreuse
  magma:  { dark: '#D9678F', light: '#9B2C5C' }, // rose
};

// 'custom' has no preset table — its colours are derived from prefs.accentCustom
// at apply time, so it's excluded from this record.
export const ACCENT_PRESETS: Record<Exclude<AccentKey, 'custom'>, AccentPreset> = {
  // ── default ─────────────────────────────────────────────────────────────
  // "Indelible" damson — mirrors the base tokens verbatim. MUST stay in sync
  // with tokens.ts (see the header comment for why).
  default: {
    dark: {
      brandFrom:      '#BB88CB',
      brandTo:        '#9A66AC',
      accentCyan:     '#BB88CB',
      accentMagenta:  '#C79BD4',
      accentAmber:    '#9A66AC',
    },
    light: {
      brandFrom:      '#6E3480',
      brandTo:        '#5A2A6A',
      accentCyan:     '#6E3480',
      accentMagenta:  '#5A2A6A',
      accentAmber:    '#6E3480',
    },
  },
  teal:   { dark: rampFrom(PRESET_SEEDS.teal.dark),   light: rampFrom(PRESET_SEEDS.teal.light) },
  sunset: { dark: rampFrom(PRESET_SEEDS.sunset.dark), light: rampFrom(PRESET_SEEDS.sunset.light) },
  aurora: { dark: rampFrom(PRESET_SEEDS.aurora.dark), light: rampFrom(PRESET_SEEDS.aurora.light) },
  ember:  { dark: rampFrom(PRESET_SEEDS.ember.dark),  light: rampFrom(PRESET_SEEDS.ember.light) },
  mint:   { dark: rampFrom(PRESET_SEEDS.mint.dark),   light: rampFrom(PRESET_SEEDS.mint.light) },
  magma:  { dark: rampFrom(PRESET_SEEDS.magma.dark),  light: rampFrom(PRESET_SEEDS.magma.light) },
};
